import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppUser } from '../../../hooks/useAppUser';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import MovieIcon from '@mui/icons-material/Movie';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import dayjs from 'dayjs';
import { useApi } from '../../../hooks/useApi';
import { usePageSize } from '../../../stores/settingsStore';

function getStatusColor(status) {
  switch (status) {
    case 'completed':
    case 'files uploaded':
      return 'success';
    case 'recording':
      return 'primary';
    case 'processing':
      return 'warning';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
}

function Recordings() {
  const navigate = useNavigate();
  const api = useApi();
  const { user } = useAppUser();
  const [page, setPage] = useState(0);
  const { rowsPerPage, setRowsPerPage } = usePageSize('recordings');
  const [search, setSearch] = useState('');

  // Include user ID in query keys so cache is per-user
  const userId = user?.id || user?._id || user?.email;

  const { data, isLoading } = useQuery({
    queryKey: ['recordings', userId, page, rowsPerPage, search],
    queryFn: () => api.get('/api/recordings', { skip: page * rowsPerPage, limit: rowsPerPage, search }),
    enabled: !!userId,
    placeholderData: (previousData) => previousData,
  });

  const recordings = data?.recordings || [];
  const total = data?.total || 0;

  // Reset page when user changes
  useEffect(() => {
    setPage(0);
  }, [userId]);


  // Reset page when search changes
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Typography variant="h4" gutterBottom>
        Recordings
      </Typography>
      <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <TextField
          fullWidth
          placeholder="Search recordings..."
          value={search}
          onChange={handleSearchChange}
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }
          }}
        />
      </Paper>
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', mb: 7 }}>
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Recording ID</TableCell>
                <TableCell>Room Name</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Composition</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : recordings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography sx={{
                      color: "text.secondary"
                    }}>No recordings found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recordings.map((recording) => (
                  <TableRow
                    key={recording._id || recording.recordingId}
                    hover
                    onDoubleClick={() => navigate(`/recordings/${recording.recordingId || recording._id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{recording.recordingId}</TableCell>
                    <TableCell>{recording.roomName}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {recording.owner || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={recording.status || 'Unknown'}
                        color={getStatusColor(recording.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const status = recording.compositionStatus;
                        if (status === 'completed') {
                          return (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                              label="Ready"
                              size="small"
                              color="success"
                              variant="filled"
                            />
                          );
                        } else if (status === 'processing' || status === 'pending') {
                          return (
                            <Chip
                              icon={<HourglassEmptyIcon sx={{ fontSize: 14 }} />}
                              label={status === 'processing' ? 'Processing' : 'Pending'}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          );
                        } else if (status === 'removed') {
                          return (
                            <Chip
                              label="Removed"
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          );
                        } else {
                          return (
                            <Typography variant="body2" sx={{
                              color: "text.secondary"
                            }}>—</Typography>
                          );
                        }
                      })()}
                    </TableCell>
                    <TableCell>
                      {dayjs(recording.timeStarted).format('MMM D, YYYY h:mm A')}
                    </TableCell>
                    <TableCell>
                      {recording.timeEnded ? (
                        `${dayjs(recording.timeEnded).diff(dayjs(recording.timeStarted), 'minute')} min`
                      ) : (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: 'success.main',
                            fontWeight: 500,
                          }}
                        >
                          {dayjs().diff(dayjs(recording.timeStarted), 'minute')} min+
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1100,
        }}
      />
    </Box>
  );
}

export default Recordings;
